import { useState } from "react";
import { useAuth } from "../lib/auth";
import { Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Spark } from "../components/Spark";
import { REGISTER } from "../constants/testIds";
import { ArrowRight, Loader2, Crown, Sparkles } from "lucide-react";

// Espelho local de PLAN_PRICES/PLAN_NAMES do backend — só pro resumo do topo.
const PLANOS = {
  gratis: { nome: "Grátis", preco: 0 },
  medio: { nome: "Médio", preco: 19.9 },
  pro: { nome: "Pro", preco: 39.9 },
};

// Aceita apelidos em inglês na URL e cai pro Grátis se vier qualquer coisa estranha.
function normalizarPlano(raw) {
  const mapa = { gratis: "gratis", free: "gratis", medio: "medio", medium: "medio", pro: "pro" };
  return mapa[(raw || "").toLowerCase()] || "gratis";
}

export default function Cadastro() {
  const { user, register, error } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plano = normalizarPlano(params.get("plano"));
  const info = PLANOS[plano];
  const pago = plano !== "gratis";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Fluxo pago: depois do cadastro a conta já existe (user setado) — esses
  // estados seguram o redirect pro Hub enquanto abrimos o checkout do MP.
  const [indoPagar, setIndoPagar] = useState(false);
  const [payAviso, setPayAviso] = useState(null);

  if (user && !indoPagar && !payAviso) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (!pago) {
      // Grátis: cria a conta, loga e o <Navigate> acima leva pro Hub.
      await register(name, email, password, plano);
      setBusy(false);
      return;
    }
    // Médio/Pro: cria a conta (sempre nasce Grátis no backend) e abre o
    // checkout do Mercado Pago — o plano só ativa via webhook, após pagar.
    setIndoPagar(true);
    const ok = await register(name, email, password, plano);
    if (!ok) {
      setIndoPagar(false);
      setBusy(false);
      return;
    }
    try {
      const { data } = await api.post("/payments/subscribe", { plano });
      window.location.href = data.init_point;
    } catch {
      setPayAviso(
        `Sua conta foi criada, mas o pagamento está indisponível no momento. ` +
        `Você já pode usar o plano Grátis normalmente e assinar o ${info.nome} depois, na aba Planos.`
      );
      setIndoPagar(false);
      setBusy(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground flex items-center justify-center p-6 relative overflow-hidden">
      {/* ambient violet glows */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 h-[400px] w-[400px] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 h-[350px] w-[350px] rounded-full bg-fuchsia-600/15 blur-[120px]" />
      </div>

      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center gap-3 mb-8">
          <Link to="/"><Spark className="h-14 w-14" /></Link>
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight">Criar sua conta</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {pago ? "Leva menos de 1 minuto." : "Grátis, sem cartão. Leva menos de 1 minuto."}
            </p>
          </div>
        </div>

        {/* Resumo do plano escolhido nos /planos */}
        <div
          data-testid="cadastro-plano-resumo"
          className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-violet-500/40 bg-violet-500/10 px-4 py-3"
        >
          <p className="text-sm">
            <span className="flex items-center gap-1.5 font-semibold">
              {plano === "pro" ? <Crown className="h-4 w-4 text-amber-400" /> : plano === "medio" ? <Sparkles className="h-4 w-4 text-violet-400" /> : null}
              Plano {info.nome}
            </span>
            <span className="text-xs text-muted-foreground">
              {pago
                ? <>R$ {info.preco.toFixed(2).replace(".", ",")}/mês — você paga depois de criar a conta</>
                : <>Grátis — sem cartão</>}
            </span>
          </p>
          <Link
            data-testid="cadastro-trocar-plano"
            to="/planos"
            className="shrink-0 text-xs font-medium text-violet-400 hover:text-violet-300 underline underline-offset-2"
          >
            Trocar plano
          </Link>
        </div>

        {payAviso ? (
          <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
            <p data-testid="cadastro-pagamento-indisponivel" className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              {payAviso}
            </p>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-medium py-3 transition-all glow-violet"
            >
              Entrar no Hub <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <div className="space-y-1.5">
            <label className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Nome</label>
            <input
              data-testid={REGISTER.nameInput}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg bg-secondary px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/50 transition"
              placeholder="Seu nome"
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Email</label>
            <input
              data-testid={REGISTER.emailInput}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg bg-secondary px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/50 transition"
              placeholder="voce@email.com"
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Senha</label>
            <input
              data-testid={REGISTER.passwordInput}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg bg-secondary px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/50 transition"
              placeholder="Mínimo 6 caracteres"
              minLength={6}
              required
            />
          </div>
          {error && (
            <div data-testid="register-error-msg" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <button
            data-testid={REGISTER.submitButton}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-medium py-3 transition-all disabled:opacity-60 glow-violet"
          >
            {busy
              ? <><Loader2 className="h-4 w-4 animate-spin" /> {indoPagar ? "Abrindo o pagamento..." : "Criando..."}</>
              : <>{pago ? "Criar conta e ir pro pagamento" : "Criar conta grátis"} <ArrowRight className="h-4 w-4" /></>}
          </button>
          {pago && (
            <p className="text-[11px] text-muted-foreground text-center">
              Pagamento seguro via Mercado Pago. O plano ativa sozinho assim que o pagamento é aprovado.
            </p>
          )}
        </form>
        )}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Já tem conta?{" "}
          <Link data-testid={REGISTER.loginLink} to="/login" className="text-violet-400 hover:text-violet-300 font-medium">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
}
